// Aba FATURAMENTO (Editar Usuário) — cobranças reais do cliente no Asaas,
// via GPS Drive Backend (nunca fala com o Asaas direto do navegador, a
// API key nem existe no frontend). Backend próprio (2026-08-27), primeiro
// real desta aba — antes 100% dados fixos. Mesmo padrão de
// gpsdrive-client-profile.service.ts.
import { gpsDriveGet, gpsDrivePatch, gpsDrivePost, gpsDriveDelete } from "@/lib/api/gpsdrive-client";

export type InvoiceStatus = "pendente" | "paga" | "vencida" | "estornada" | "outro";
export type InvoiceBillingType = "BOLETO" | "PIX" | "CREDIT_CARD" | "UNDEFINED";
// "avulsa" = cobrança única; "recorrencia" = gerada automaticamente pelo
// Asaas a partir de uma assinatura — ver observação na aba FATURAMENTO.
export type InvoiceOrigin = "avulsa" | "recorrencia";

export interface Invoice {
  id: string;
  status: InvoiceStatus;
  billingType: string;
  value: number;
  dueDate: string;
  paymentDate: string | null;
  description: string | null;
  invoiceUrl: string | null;
  bankSlipUrl: string | null;
  origin: InvoiceOrigin;
}

// Tela Financeiro do app do cliente (PWA) — GET /users/me/billing-status,
// a ÚNICA fonte de verdade sobre acesso suspenso (ver
// BillingService#getMyStatus no backend). `suspended` nunca é decidido
// aqui no front — só reflete o que o backend calculou a partir das
// faturas reais do Asaas + asaasSuspensionDays configurado pela empresa.
export interface BillingStatus {
  suspended: boolean;
  overdueInvoice: Invoice | null;
  asaasCustomerId: string | null;
}

export interface InvoiceListResponse {
  // null = este cliente nunca foi sincronizado com o Asaas ainda (salve a
  // aba CLIENTE com a integração ativa primeiro) OU a última tentativa
  // falhou — nesse segundo caso `asaasSyncError` abaixo vem preenchido com
  // o motivo (ex.: e-mail inválido).
  asaasCustomerId: string | null;
  asaasSyncError: string | null;
  invoices: Invoice[];
  hasMore: boolean;
  totalCount: number;
}

export interface CreateInvoiceInput {
  value: number;
  dueDate: string;
  billingType: InvoiceBillingType;
  description?: string;
}

export type SubscriptionCycle =
  | "WEEKLY"
  | "BIWEEKLY"
  | "MONTHLY"
  | "BIMONTHLY"
  | "QUARTERLY"
  | "SEMIANNUALLY"
  | "YEARLY";

export interface Subscription {
  id: string;
  cycle: string;
  value: number;
  nextDueDate: string;
}

// "Recorrência" — uma única assinatura no Asaas cobrindo todos os
// veículos selecionados na tela (valor somado). Juros/multa/desconto são
// opcionais no Asaas; omitidos daqui quando o operador não preenche.
export interface CreateSubscriptionInput {
  cycle: SubscriptionCycle;
  billingType: InvoiceBillingType;
  value: number;
  nextDueDate: string;
  description?: string;
  interestValue?: number;
  fineType?: "FIXED" | "PERCENTAGE";
  fineValue?: number;
  discountDays?: number;
  discountType?: "FIXED" | "PERCENTAGE";
  discountValue?: number;
}

export interface UpdateInvoiceInput {
  value?: number;
  dueDate?: string;
  billingType?: InvoiceBillingType;
  description?: string;
}

// Botão "Pagar com PIX" (Faturas do app do cliente) — só existe para
// fatura com billingType "PIX" (o backend devolve 422 pras demais).
// encodedImage é a imagem do QR code em base64 (sem prefixo data:).
export interface PixQrCode {
  encodedImage: string;
  payload: string;
  expirationDate: string | null;
}

export const gpsDriveBillingService = {
  myStatus: () => gpsDriveGet<BillingStatus>("/api/v1/users/me/billing-status"),
  list: (userId: string, page: { limit: number; offset: number }) =>
    gpsDriveGet<InvoiceListResponse>(
      `/api/v1/users/${userId}/invoices?limit=${page.limit}&offset=${page.offset}`,
    ),
  create: (userId: string, input: CreateInvoiceInput) =>
    gpsDrivePost<Invoice>(`/api/v1/users/${userId}/invoices`, input),
  update: (userId: string, invoiceId: string, input: UpdateInvoiceInput) =>
    gpsDrivePatch<Invoice>(`/api/v1/users/${userId}/invoices/${invoiceId}`, input),
  remove: (userId: string, invoiceId: string) =>
    gpsDriveDelete<void>(`/api/v1/users/${userId}/invoices/${invoiceId}`),
  createSubscription: (userId: string, input: CreateSubscriptionInput) =>
    gpsDrivePost<Subscription>(`/api/v1/users/${userId}/subscriptions`, input),
  pixQrCode: (userId: string, invoiceId: string) =>
    gpsDriveGet<PixQrCode>(`/api/v1/users/${userId}/invoices/${invoiceId}/pix-qrcode`),
};
